import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import HorizontalMenu from '../components/common/horizontalMenu';
import { Brain, Send, Heart, Sparkles, AlertCircle, FileText } from 'lucide-react';
import Breadcrumbs from '../components/common/breadcrumbs';
import { ProjectAndCustomer } from '../project/projectType';
import { LineItem } from '../estimate/estimateType';
import { formatAddress } from '../lib/global_function';
import { getSessionData } from '../lib/global_function';
import SmartEstimateAssistantApi from './smartEstimateAssistantApi';
import { SmartEstimateChatMessage, EstimateData as AIEstimateData } from './smartEstimateAssistantType';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/select';
import { Label } from '../components/ui/label';
import ProjectApi from '../project/projectApi';
import Spinner from '../components/ui/spinner';

interface ExistingEstimateData {
  name: string;
  description: string;
  terms_and_conditions: string;
  time_to_complete_hr: number;
  time_to_complete_day: number;
  tax_rate: number;
  discount_usd: number;
}
//********************************************************
//this page is not used. The original thought is to store the chat history in the local storage in case the user refreshes the browser. 
// Now //we just make the call to the backend to get the conversation history based on the conversation_id in the URL params. 
// So this page is still here for reference but not used in the actual flow.
//
const formatMessage = (content: string | any[]): string => {
  // Handle array content (Claude API format)
  if (Array.isArray(content)) {
    const textContent = content
      .filter(block => block.type === 'text')
      .map(block => block.text)
      .join('\n');
    return formatMessage(textContent); // Recursively format the extracted text
  }
  
  // Handle string content
  let formatted = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/^- (.+)$/gm, '• $1');
  return formatted;
};



const TypingIndicator = () => (
  <div className="flex justify-start mb-4">
    <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-white shadow-sm border border-gray-200 flex gap-1.5 items-center">
      <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '-0.32s' }} />
      <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '-0.16s' }} />
      <div className="w-2 h-2 rounded-full bg-primary animate-bounce" />
    </div>
  </div>
);

const SmartAIEstimateAssistantPage = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Estimate', path: '/main/project/estimate' },
    { label: 'Smart Assistant', path: '/main/estimateEngine/smartAssistant' },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // State from previous page
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<ProjectAndCustomer | null>(null);
  const [existingEstimate, setExistingEstimate] = useState<ExistingEstimateData | null>(null);
  const [existingLineItems, setExistingLineItems] = useState<LineItem[]>([]);
  const [accountId, setAccountId] = useState<string | null>(null);

  // Project selection state
  const [projectsAndCustomers, setProjectsAndCustomers] = useState<ProjectAndCustomer[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  // Chat state
  const [conversationId, setConversationId] = useState<string>('');
  const [messages, setMessages] = useState<SmartEstimateChatMessage[]>([]);
  const [currentInput, setCurrentInput] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Message tracking
  const [messageCount, setMessageCount] = useState<number>(0);
  const [remainingMessages, setRemainingMessages] = useState<number>(15);

  // AI Estimate data
  const [aiEstimateData, setAiEstimateData] = useState<AIEstimateData | null>(null);
  const [isEstimateReady, setIsEstimateReady] = useState<boolean>(false);
  
  // Conversation restoration
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Get account_id from session storage
  useEffect(() => {
    const sessionData = getSessionData(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
    const accountIdFromSession = String(sessionData?.account_id);

    if (typeof accountIdFromSession === 'string' && accountIdFromSession.trim()) {
      setErrorMessage(null);
      setAccountId(accountIdFromSession);
    } else {
      setErrorMessage('Account not found. You must be logged in first.');
    }
  }, []);

  // Fetch projects and customers
  useEffect(() => {
    const fetchProjectsAndCustomers = async () => {
      setIsLoadingProjects(true);
      try {
        const fetchedProjectsAndCustomers = await ProjectApi.getProjectsAndCustomers();
        setProjectsAndCustomers(fetchedProjectsAndCustomers);

        // Check if project was passed from previous page
        if (location.state) {
          const projectIdFromState = location.state.selectedProjectId;
          const customerFromState = location.state.selectedCustomer;

          if (projectIdFromState && customerFromState) {
            setSelectedProjectId(projectIdFromState);
            setSelectedCustomer(customerFromState);
          }

          setExistingEstimate(location.state.existingEstimate);
          setExistingLineItems(location.state.existingLineItems || []);
        }
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load projects.');
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjectsAndCustomers();
  }, [location.state]);

// Restore conversation from URL params
useEffect(() => {
  const restoreConversation = async () => {
    const conversationIdFromUrl = searchParams.get('conversation_id');
    
    // If we have a conversation_id in URL but no messages, fetch the conversation
    if (conversationIdFromUrl && messages.length === 0 && !isLoadingConversation) {
      setIsLoadingConversation(true);
      setConversationId(conversationIdFromUrl);
      
      try {
        const conversationData = await SmartEstimateAssistantApi.getConversation(conversationIdFromUrl);
        
        // Restore chat messages - normalize the content format
        const normalizedMessages = conversationData.chat.messages.map(msg => ({
          role: msg.role,
          content: Array.isArray(msg.content) 
            ? msg.content.filter((block: any) => block.type === 'text').map((block: any) => block.text).join('\n')
            : msg.content
        }));
        
        setMessages(normalizedMessages);
        setMessageCount(conversationData.chat.message_count);
        setRemainingMessages(conversationData.chat.remaining_messages);
        
        // Set the actual project_id from the conversation  ← UPDATE THIS
        setSelectedProjectId(conversationData.project.project_id.toString());
        
        // Restore customer data (map to ProjectAndCustomer format)
        const restoredCustomer: ProjectAndCustomer = {
          project_id: conversationData.project.project_id.toString(),  // ← UPDATE THIS
          name: conversationData.project.name,
          project_description: conversationData.project.description,
          project_type: conversationData.project.type,
          estimated_sum: 0,
          estimated_duration_days: 0,
          start_date: '',
          end_date: '',
          company_code: '',
          company_name: '',
          customer_id: '0',
          customer_first_name: conversationData.customer.first_name,
          customer_last_name: conversationData.customer.last_name,
          customer_address_1: conversationData.customer.address_1,
          customer_address_2: conversationData.customer.address_2,
          customer_city: conversationData.customer.city,
          customer_state: conversationData.customer.state,
          customer_zip: conversationData.customer.zip,
          customer_phone: conversationData.customer.phone,
          customer_email: conversationData.customer.email,
          created_by_account_id: '',
          created_at: '',
          updated_at: '',
        };
        
        setSelectedCustomer(restoredCustomer);
        
        // Try to find matching project in projectsAndCustomers if loaded
        if (projectsAndCustomers.length > 0) {
          const matchingProject = projectsAndCustomers.find(
            (p) => p.project_id.toString() === conversationData.project.project_id.toString()
          );
          if (matchingProject) {
            setSelectedProjectId(matchingProject.project_id.toString());
            setSelectedCustomer(matchingProject); // Use the full project data
          }
        }
        
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to restore conversation.');
        // Remove invalid conversation_id from URL
        searchParams.delete('conversation_id');
        setSearchParams(searchParams);
      } finally {
        setIsLoadingConversation(false);
      }
    }
  };

  // Only run after projects are loaded
  if (!isLoadingProjects) {
    restoreConversation();
  }
}, [searchParams, messages.length, isLoadingProjects, projectsAndCustomers, isLoadingConversation, setSearchParams]);


  // Handle project selection
  const handleProjectChange = (projectId: string) => {
    if (!projectId) {
      setSelectedProjectId(null);
      setSelectedCustomer(null);
      return;
    }

    setSelectedProjectId(projectId);
    const selected = projectsAndCustomers.find(
      (projCust) => projCust.project_id.toString() === projectId
    );
    setSelectedCustomer(selected || null);
  };

  // ******Handle sending message
  const handleSendMessage = async () => {
    if (!currentInput.trim() || !accountId) return;

    setIsProcessing(true);
    setErrorMessage(null);

    // Create user message for display (without location info)
    const userMessage: SmartEstimateChatMessage = {
      role: 'user',
      content: currentInput.trim(),
    };

    // Display only the user's original message in the UI
    setMessages((prev) => [...prev, userMessage]);
    setCurrentInput('');

    try {
      const response = await SmartEstimateAssistantApi.estimateChat({
        conversation_id: conversationId,
        account_id: Number(accountId),
        project_id: selectedProjectId || '',
        estimate_location: selectedCustomer
        ? {
            city: selectedCustomer.customer_city || undefined,
            state: selectedCustomer.customer_state || undefined,
            zip: selectedCustomer.customer_zip || undefined,
          }
        : undefined,
        messages: [userMessage],
      });

      // Store conversation_id in URL params
      if (!conversationId && response.conversation_id) {
        const newConversationId = response.conversation_id.toString();
        setConversationId(newConversationId);
        setSearchParams({ conversation_id: newConversationId });
      }

      // Update message counts
      setMessageCount(response.message_count);
      setRemainingMessages(response.remaining_messages);

      const assistantMessage: SmartEstimateChatMessage = {
        role: 'assistant',
        content: response.message,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Check if estimate is ready
      if (response.status === 'estimate_ready' && response.estimate_data) {
        setAiEstimateData(response.estimate_data);
        setIsEstimateReady(true);
        //console.log('Estimate ready:', response.estimate_data);
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFinalView = () => {
    if (aiEstimateData && selectedCustomer) {
      navigate('/main/project/estimate/smartgeneratedestimate', {
        state: {
          estimateData: aiEstimateData,
          selectedCustomer: selectedCustomer,
          conversationId: conversationId,
        }
      });
    }
  };

  if (isLoadingProjects || isLoadingConversation) {
    return (
      <>
        <HorizontalMenu />
        <Spinner />
      </>
    );
  }

  return (
    <>
      <HorizontalMenu />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mb-4">
          <h1 className="text-2xl font-semibold text-foreground mb-2 flex items-center gap-2">
            <Sparkles className="size-7 text-primary" />
            Smart AI Estimate
          </h1>
        </div>

        {errorMessage && (
          <Alert variant="error" className="mb-6">
            <div className="flex items-center gap-3">
              {errorMessage}
            </div>
          </Alert>
        )}

        {/* Project Selection Dropdown - Show only on initial load when no project selected yet */}
        {!selectedCustomer && messages.length === 0 && (
          <div className="mb-6 p-4 md:p-6 bg-white border border-gray-200 rounded-lg">
            <h2 className="text-base md:text-lg font-semibold text-foreground mb-4">Select a Project</h2>
            <div className="max-w-2xl">
              <Label htmlFor="projectSelect">
                Project <span className="text-destructive">*</span>
              </Label>
              <Select
                id="projectSelect"
                value={selectedProjectId || ''}
                onChange={(e) => handleProjectChange(e.target.value)}
              >
                <option value="">Select a project...</option>
                {projectsAndCustomers.map((projCust) => (
                  <option key={projCust.project_id} value={projCust.project_id.toString()}>
                    {projCust.name}
                    {projCust.project_description
                      ? ` - ${projCust.project_description.substring(0, 100)}`
                      : ''}
                  </option>
                ))}
              </Select>
              {projectsAndCustomers.length === 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  No projects available. Please create a project first.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Project Selection - Show when changing project */}
        {!selectedCustomer && messages.length > 0 && (
          <div className="mb-4 p-4 md:p-6 bg-blue-50 border-2 border-blue-300 rounded-lg">
            <h2 className="text-base md:text-lg font-semibold text-foreground mb-4">Select a Different Project</h2>
            <div className="max-w-2xl">
              <Label htmlFor="projectSelectChange">
                Project <span className="text-destructive">*</span>
              </Label>
              <Select
                id="projectSelectChange"
                value={selectedProjectId || ''}
                onChange={(e) => handleProjectChange(e.target.value)}
              >
                <option value="">Select a project...</option>
                {projectsAndCustomers.map((projCust) => (
                  <option key={projCust.project_id} value={projCust.project_id.toString()}>
                    {projCust.name}
                    {projCust.project_description
                      ? ` - ${projCust.project_description.substring(0, 100)}`
                      : ''}
                  </option>
                ))}
              </Select>
              {projectsAndCustomers.length === 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  No projects available. Please create a project first.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Main Content - Always show after initial project selection */}
        {(selectedCustomer || messages.length > 0) && (
          <div className="space-y-4">
            {/* Project Information - Now above chat with Change Project button inside */}
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
              <div className="bg-primary text-white px-3 md:px-4 py-2 font-semibold flex items-center justify-between gap-2">
                <span className="text-sm md:text-base">Project & Customer Information</span>
                <Button
                  variant="inverted"
                  size="medium"
                  onClick={() => {
                    setSelectedProjectId(null);
                    setSelectedCustomer(null);
                    // Don't clear messages and conversation - keep chat visible
                  }}
                  className="!bg-white !text-primary hover:!bg-gray-100 !py-1 !px-2 md:!px-3 !text-xs md:!text-sm"
                >
                  Change Project
                </Button>
              </div>

              <div className="p-4">
                {selectedCustomer ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {/* Left Column - Project Data */}
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-primary mb-2">Project</h3>
                      <div className="text-xs text-gray-600">
                        <strong>Name:</strong>&nbsp;<span>{selectedCustomer.name}</span>
                      </div>
                      {selectedCustomer.project_description && (
                        <div className="text-xs text-gray-600">
                          <strong>Description:</strong>&nbsp;<span>{selectedCustomer.project_description}</span>
                        </div>
                      )}
                    </div>

                    {/* Right Column - Customer Data */}
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-primary mb-2">Customer</h3>
                      <div className="text-xs text-gray-600">
                        <strong>Name:</strong>&nbsp;<span>{selectedCustomer.customer_first_name} {selectedCustomer.customer_last_name}</span>
                      </div>
                      <div className="text-xs text-gray-600">
                        <strong>Address:</strong>&nbsp;<span>
                          {formatAddress(
                            selectedCustomer.customer_address_1,
                            '',
                            selectedCustomer.customer_city,
                            selectedCustomer.customer_state,
                            selectedCustomer.customer_zip
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    <p className="text-sm">Please select a project above to continue</p>
                  </div>
                )}
              </div>
            </div>

            {/* Chat Interface - Always visible once project selected */}
            <div className="mb-10">
              <div className="border border-gray-300 rounded-lg bg-white flex flex-col h-[500px] md:h-[600px]">
                {/* Chat Header */}
                <div className="px-3 md:px-4 py-3 border-b border-gray-300 bg-gray-50 rounded-t-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <Brain size={20} className="text-primary shrink-0" />
                    <div>
                      <div className="font-semibold text-sm md:text-base">Smart Assistant</div>
                      <div className="text-xs text-gray-600">
                        {conversationId ? `Session: ${conversationId}` : 'New conversation'}
                      </div>
                    </div>
                  </div>

                  {conversationId && (
                    <div
                      className={`text-xs px-2 py-1 rounded font-semibold ${remainingMessages <= 3
                        ? 'bg-yellow-100 border border-yellow-400 text-yellow-800'
                        : 'bg-green-100 border border-green-400 text-green-800'
                        }`}
                    >
                      {remainingMessages} messages left
                    </div>
                  )}
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-3 md:p-5 bg-gray-50">
                  {!selectedCustomer ? (
                    <div className="text-center text-gray-400 mt-8 md:mt-12 px-4">
                      <AlertCircle size={48} className="mx-auto mb-3 opacity-50" />
                      <p className="italic text-sm md:text-base">Please select a project to continue chatting</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-gray-400 mt-8 md:mt-12 px-4">
                      <Brain size={48} className="mx-auto mb-3 opacity-50" />
                      <p className="italic text-sm md:text-base">Start by describing your project...</p>
                      <p className="text-xs mt-2">Example: "I am doing a project of renovating a 150 sq ft bathroom"</p>
                    </div>
                  ) : (
                    <>
                      {messages.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex mb-4 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[85%] md:max-w-[75%] px-3 md:px-4 py-2.5 md:py-3 shadow-sm ${msg.role === 'user'
                              ? 'rounded-2xl rounded-br-sm bg-primary text-white'
                              : 'rounded-2xl rounded-bl-sm bg-white text-gray-800 border border-gray-200'
                              }`}
                          >
                            <div
                              className="whitespace-pre-wrap leading-relaxed text-xs md:text-sm"
                              dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                            />
                          </div>
                        </div>
                      ))}

                      {isProcessing && <TypingIndicator />}
                    </>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="px-3 md:px-4 py-3 border-t border-gray-300 bg-white rounded-b-lg">
                  {isEstimateReady ? (
                    <div className="flex justify-center">
                      <Button
                        onClick={handleFinalView}
                        size="medium"
                        className="bg-primary hover:bg-primary-hover active:bg-primary-active text-white w-full md:w-auto"
                      >
                        <FileText size={20} />
                        Final View
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2 md:gap-3 items-stretch sm:items-end">
                      <textarea
                        value={currentInput}
                        onChange={(e) => setCurrentInput(e.target.value)}
                        onKeyUp={handleKeyPress}
                        className="flex-1 px-3 py-2.5 border border-gray-300 rounded-2xl resize-none text-xs md:text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 min-h-[60px] md:min-h-[80px] max-h-[120px] md:max-h-[160px]"
                        placeholder={!selectedCustomer ? "Select a project to start chatting..." : "Type your message... (Press Enter to send, Shift+Enter for new line)"}
                        disabled={!selectedCustomer || isProcessing || remainingMessages === 0}
                        rows={1}
                      />
                      <Button
                        onClick={handleSendMessage}
                        disabled={!selectedCustomer || isProcessing || !currentInput.trim() || remainingMessages === 0}
                        size="medium"
                        className={`w-full sm:w-auto ${
                          !selectedCustomer || isProcessing || !currentInput.trim() || remainingMessages === 0
                            ? 'bg-gray-400 text-white'
                            : 'bg-primary hover:bg-primary-hover active:bg-primary-active text-white'
                        }`}
                      >
                        {isProcessing ? (
                          <>Processing...</>
                        ) : (
                          <>
                            <Send size={16} />
                            Send
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SmartAIEstimateAssistantPage;